/**
 * Device Manager Utility
 * Handles audio and video device detection, selection, and management
 */

/**
 * Get all available audio input devices (microphones)
 * @returns {Promise<Array>} Array of audio input devices
 */
export const getAudioDevices = async () => {
  try {
    // Request microphone permission first
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => track.stop()); // Stop the stream immediately
    
    // Get all audio input devices
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = devices.filter(device => device.kind === 'audioinput');
    
    return audioInputs;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all available video input devices (cameras)
 * @returns {Promise<Array>} Array of video input devices
 */
export const getVideoDevices = async () => {
  try {
    // Request camera permission first
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach(track => track.stop()); // Stop the stream immediately
    
    // Get all video input devices
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoInputs = devices.filter(device => device.kind === 'videoinput');
    
    return videoInputs;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all available devices (both audio and video)
 * @returns {Promise<Object>} Object containing audio and video devices
 */
export const getAllDevices = async () => {
  try {
    // Request both audio and video permissions
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
    stream.getTracks().forEach(track => track.stop()); // Stop the stream immediately
    
    // Get all devices
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = devices.filter(device => device.kind === 'audioinput');
    const videoInputs = devices.filter(device => device.kind === 'videoinput');
    
    return {
      audio: audioInputs,
      video: videoInputs
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Get the default device from a list of devices
 * @param {Array} devices - Array of devices
 * @param {string} type - Device type ('audio' or 'video')
 * @returns {Object|null} Default device or first available device
 */
export const getDefaultDevice = (devices, type = 'audio') => {
  if (!devices || devices.length === 0) {
    return null;
  }
  
  // Look for device with deviceId === 'default'
  const defaultDevice = devices.find(device => device.deviceId === 'default');
  if (defaultDevice) {
    return defaultDevice;
  }
  
  // Fallback to first device
  return devices[0];
};

/**
 * Create device constraints for getUserMedia
 * @param {string} deviceId - Device ID
 * @param {string} type - Device type ('audio' or 'video')
 * @param {Object} additionalConstraints - Additional constraints
 * @returns {Object} Media constraints
 */
export const createDeviceConstraints = (deviceId, type, additionalConstraints = {}) => {
  const constraints = {
    [type]: {
      deviceId: { exact: deviceId },
      ...additionalConstraints
    }
  };
  
  return constraints;
};

/**
 * Handle device change events
 * @param {Function} callback - Callback function to execute on device change
 * @returns {Function} Cleanup function
 */
export const setupDeviceChangeListener = (callback) => {
  const handleDeviceChange = () => {
    callback();
  };
  
  navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
  
  // Return cleanup function
  return () => {
    navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
  };
};

/**
 * Check if a device is still available in the current device list
 * @param {Object} selectedDevice - Currently selected device
 * @param {Array} availableDevices - List of available devices
 * @returns {boolean} True if device is still available
 */
export const isDeviceAvailable = (selectedDevice, availableDevices) => {
  if (!selectedDevice || !availableDevices) {
    return false;
  }
  
  return availableDevices.some(device => device.deviceId === selectedDevice.deviceId);
};

/**
 * Get device error message based on error type
 * @param {Error} error - Error object
 * @param {string} deviceType - Type of device ('audio' or 'video')
 * @returns {string} User-friendly error message
 */
export const getDeviceErrorMessage = (error, deviceType = 'audio') => {
  const deviceName = deviceType === 'audio' ? 'microphone' : 'camera';
  
  if (error.name === 'NotAllowedError') {
    return `${deviceName.charAt(0).toUpperCase() + deviceName.slice(1)} access denied. Please allow ${deviceName} access and refresh the page.`;
  } else if (error.name === 'NotFoundError') {
    return `No ${deviceName} detected. Please connect a ${deviceName} and refresh the page.`;
  } else {
    return `Error accessing ${deviceName} devices. Please check your ${deviceName} connection.`;
  }
};

/**
 * Create a device stream for testing/preview
 * @param {string} deviceId - Device ID
 * @param {string} type - Device type ('audio' or 'video')
 * @param {Object} constraints - Additional constraints
 * @returns {Promise<MediaStream>} Media stream
 */
export const createDeviceStream = async (deviceId, type, constraints = {}) => {
  const deviceConstraints = createDeviceConstraints(deviceId, type, constraints);
  return await navigator.mediaDevices.getUserMedia(deviceConstraints);
};

/**
 * Stop all tracks in a media stream
 * @param {MediaStream} stream - Media stream to stop
 */
export const stopStream = (stream) => {
  if (stream && stream.getTracks) {
    stream.getTracks().forEach(track => track.stop());
  }
};